import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { Calendar } from "@shared/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import { useDistribution, useDistributionExport, useUpdatePrintStatus } from "../../hooks";
import { ActionButton } from "./ActionButton";
import { StatusBadge } from "./StatusBadge";

interface DistributionTableProps {
  activeFilter: string | null;
  onCycleClick: (cycle: any) => void;
}

interface CycleData {
  totalBillCount: number;
  eBillCount: number;
  paperBillCount: number;
  deliveredEBillCount: number;
  failedEBillCount: number;
  deliveredPaperBill: number;
  lapsedDays: number;
  runId: number;
  cycleName: string;
  cycleId: number;
  invoiceStartDate: string;
  invoiceEndDate: string;
  dispatchedDate: string;
  dispatchStatus: string | null;
}

interface ApiResponse {
  page_number: number;
  count: number;
  next: string | null;
  previous: string | null;
  result: CycleData[];
}

// Channel display configuration with order and colors
const channelConfig = [
  {
    id: "e_bill",
    label: "E-Bill",
    color: "bg-blue-500",
    textColor: "text-blue-500",
  },
  {
    id: "paper_bill",
    label: "Paper",
    color: "bg-amber-500",
    textColor: "text-amber-500",
  },
];

export function DistributionTable({
  activeFilter,
  onCycleClick,
}: DistributionTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [editingCycleId, setEditingCycleId] = useState<number | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const { data: distributionData } = useDistribution({
    page: currentPage,
    limit: 50,
    search_data: searchTerm,
    sort_field: sortConfig?.field,
    sort_direction: sortConfig?.direction,
    active_filter: activeFilter,
  });

  const exportFilters = useMemo(() => {
    return {
      search_data: searchTerm,
      sort_field: sortConfig?.field,
      sort_direction: sortConfig?.direction,
      active_filter: activeFilter,
      // Note: No page and limit for export to get all data
    };
  }, [searchTerm, sortConfig?.field, sortConfig?.direction, activeFilter]);

  const { exportCSV, isExporting } = useDistributionExport(exportFilters);

  // Flatten the paginated response data
  const flattenedCycles = useMemo(() => {
    if (!distributionData?.result) return [];
    return distributionData.result;
  }, [distributionData]);

  const itemsPerPage = 10;

  // Determine status based on data
  const getStatus = (cycle: CycleData) => {
    if (cycle.dispatchStatus === "completed") return "completed";
    if (cycle.dispatchStatus === "in-progress" || cycle.dispatchedDate)
      return "in-progress";
    return "not-started";
  };

  // Get channel data for a specific channel
  const getChannelData = (cycle: CycleData, channelId: string) => {
    switch (channelId) {
      case "e_bill":
        return {
          opted: cycle.eBillCount,
          delivered: cycle.deliveredEBillCount,
          failed: cycle.failedEBillCount,
        };
      case "paper_bill":
        return {
          opted: cycle.paperBillCount,
          delivered: cycle.deliveredPaperBill,
          failed: 0,
        };
      default:
        return { opted: 0, delivered: 0, failed: 0 };
    }
  };

  // Table columns configuration
  const distributionColumns = [
    {
      key: "cycleName",
      header: "Billing Cycle",
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "period",
      header: "Period",
      render: (value: any, row: CycleData) => {
        const startRaw = row?.invoiceStartDate;
        const endRaw = row?.invoiceEndDate;

        if (!startRaw || !endRaw) return "-";

        return `${startRaw} - ${endRaw}`;
      },
    },
    {
      key: "totalBillCount",
      header: "Total",
      headerClassName: "text-right",
      render: (value: number) => (
        <span className="text-right">{value.toLocaleString()}</span>
      ),
    },
    {
      key: "dispatchedDate",
      header: "Dispatch Date",
      render: (value: string, row: CycleData) => {
        const isEditing = editingCycleId === row.runId;
        const dispatchDate = value ? new Date(value) : new Date();

        return (
          <div onClick={(e) => e.stopPropagation()}>
            {isEditing ? (
              <Popover
                open={isEditing}
                onOpenChange={(open) => !open && setEditingCycleId(null)}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {value ? format(dispatchDate, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0 z-50 pointer-events-auto"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={selectedDate || dispatchDate}
                    onSelect={(date) => handleEditDate(row, date)}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <div
                className="flex items-center space-x-2 cursor-pointer text-primary hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingCycleId(row.runId);
                }}
              >
                <span>{value ? format(dispatchDate, "PPP") : "Not set"}</span>
                <CalendarIcon className="h-4 w-4" />
              </div>
            )}
          </div>
        );
      },
    },
    ...channelConfig.map((channel) => ({
      key: `channel_${channel.id}`,
      header: (
        <div className="flex flex-col items-center gap-1">
          <span>{channel.label}</span>
          <div className={`h-1.5 w-8 rounded-full ${channel.color}`}></div>
        </div>
      ),
      headerClassName: "text-center whitespace-nowrap",
      render: (value: any, row: CycleData) => {
        const channelData = getChannelData(row, channel.id);
        return (
          <div className="px-2 py-1 text-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center">
                    <span
                      className={`text-xs font-medium ${channel.textColor}`}
                    >
                      {channelData.opted}/{channelData.delivered}/
                      {channelData.failed}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-xs space-y-1">
                    <div>
                      <span className="font-semibold">Opted:</span>{" "}
                      {channelData.opted}
                    </div>
                    <div>
                      <span className="font-semibold">Delivered:</span>{" "}
                      {channelData.delivered}
                    </div>
                    <div>
                      <span className="font-semibold">Failed:</span>{" "}
                      {channelData.failed}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    })),
    {
      key: "dispatchStatus",
      header: "Status",
    },
    {
      key: "lapsedDays",
      header: "Lapsed Days",
      render: (value: number) => {
        if (value === 0) return "-";
        return <StatusBadge status="warning" label={`${value} days`} />;
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (value: any, row: CycleData) => {
        const status = getStatus(row);
        return (
          <div
            className="flex flex-row gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionButton
              type="edit-date"
              onClick={() => setEditingCycleId(row.runId)}
              iconOnly
            />

            <ActionButton
              type="view"
              onClick={() => handleViewConsumers(row)}
              iconOnly
            />
          </div>
        );
      },
    },
  ];

  // Sort options for the table
  const sortOptions = [
    {
      value: "cycle_name",
      label: "Billing Cycle",
    },
    {
      value: "total_bill_count",
      label: "Total Bills",
    },
    {
      value: "dispatched_date",
      label: "Dispatch Date",
    },
  ];

  // Filter options for the table
  const filterOptions = [
    {
      value: "pendingDispatch",
      label: "Pending Dispatch",
    },
    {
      value: "deliveryFailures",
      label: "Delivery Failures",
    },
    {
      value: "dispatchDelayed",
      label: "Dispatch Delayed",
    },
  ];

  // Handle sorting
  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (searchValue: string, paramName: string = "search") => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  // Handle filter
  const handleFilter = (filter: string) => {
    setCurrentPage(1);
  };

  const handleExport = () => {
    exportCSV();
  };
  const updatePrintStatus = useUpdatePrintStatus();

  const handleEditDate = (row: any, date: Date) => {
    if (!date) return;
    const formattedDate = format(date, "yyyy-MM-dd");

    updatePrintStatus.mutate(
      {
        runId: row.runId,
        payload: {
          dispatch_date: formattedDate,
          dispatch_status: row.dispatchStatus,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey:
              QueryKeyFactory.module.bx.printingDistribution.distributionList(),
          });
        },
      }
    );

    // Implement date update logic here
    setEditingCycleId(null);
    setSelectedDate(null);
  };

  const handleDownloadZip = (runId: number) => {};

  const handleViewConsumers = (row: any) => {
    onCycleClick(row);
    navigate(`/bx/printing-distribution/consumers/${row.runId}`);
  };

  return (
    <div className="space-y-6">
      <GenericTable
        data={flattenedCycles}
        columns={distributionColumns}
        rowKey="runId"
        emptyMessage="No distribution cycles found"
        searchPlaceholder="Search cycles..."
        searchParamName="search"
        searchDebounceMs={500}
        onSearch={handleSearch}
        sortConfig={sortConfig}
        onSort={handleSort}
        filterOptions={filterOptions}
        onFilter={handleFilter}
        onExport={handleExport}
        exportLabel="Export Distribution Data"
        pagination={{
          currentPage: distributionData?.page_number || 1,
          totalPages: Math.ceil((distributionData?.count || 0) / itemsPerPage),
          totalItems: distributionData?.count || 0,
          itemsPerPage: itemsPerPage,
          startItem: distributionData?.page_number
            ? (distributionData.page_number - 1) * itemsPerPage + 1
            : 0,
          endItem: distributionData?.page_number
            ? Math.min(
                distributionData.page_number * itemsPerPage,
                distributionData.count || 0
              )
            : 0,
        }}
        onPageChange={handlePageChange}
        // getRowClassName={(row, index) => {
        //   const totalFailed =
        //     row.failed_e_bill_count +
        //     (row.paper_bill_count - row.delivered_paper_bill);
        //   const totalOpted = row.e_bill_count + row.paper_bill_count;
        //   const allFailed = totalOpted > 0 && totalFailed === totalOpted;
        //   return allFailed ? "bg-destructive/10" : "";
        // }}
      />
    </div>
  );
}
